import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiRequest } from "@/lib/queryClient";
import { cashScopeKey } from "./use-branch-cash";

export type CashReceiptOption = { id: string; reference: string; supplierName: string; branchName: string; receiptDate: string; total: string };
export type CashReceipt = CashReceiptOption & {
  loggedAt: string; status: string; paid: string; remaining: string; returnAdjustment?: string; hasUnpricedItems?: boolean;
  items: { id: string; name: string; quantity: string | number; unit: string; unitCost: string; lineTotal: string }[];
  payments: { id: string; reference: string; amount: string; cashAmount?: string; allocationAmount?: string; direction: "in" | "out"; occurredAt: string; actorName: string }[];
  otherPayments?: { paymentId: string; amount: string; status: string; paymentDate: string; reference: string }[];
  photos: { url: string }[]; confirmedBy: string | null; confirmedAt: string | null;
};
export function useCashReceipt<T = CashReceipt>(id: string, branches: string[], options = false, enabled = true) {
  const { currentWorkspace, user, roles } = useAuth();
  return useQuery<T>({
    queryKey: ["branch-cash", options ? "receipt-options" : "receipt-document", ...cashScopeKey(currentWorkspace?.id, user, roles.find(r => r.id === user?.roleId)?.permissions.map(p => p.id)), [...branches].sort().join(","), id],
    enabled: enabled && !!id && !!currentWorkspace?.id && branches.length > 0,
    queryFn: async () => (await apiRequest("GET", options ? `/api/cash/movements/${encodeURIComponent(id)}/receipt-options` : `/api/cash/documents/receipt/${encodeURIComponent(id)}`)).json(),
  });
}
export function useAttachCashReceipt(id: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: async (data: { receiptId: string; reason: string }) => {
      if (!navigator.onLine) throw Error("Connect before attaching a receipt. Nothing has been queued.");
      return (await apiRequest("POST", `/api/cash/movements/${encodeURIComponent(id)}/receipt`, data)).json();
    },
    onSuccess: () => cache.invalidateQueries({ queryKey: ["branch-cash"] }),
  });
}